<template>
  <div class="constructor-tab">
    <div class="chart-layout">
      <div class="chart-settings">
        <div class="settings-title">Конструктор отчёта</div>

        <div class="settings-section">
          <div class="settings-label">Ось X (колонки)</div>
          <select v-model="xDim" class="dim-select">
            <option v-for="d in dimOptions" :key="d.id" :value="d.id">{{ d.label }}</option>
          </select>
          <template v-if="isTimeX">
            <div class="settings-label sub">Шаг корзин</div>
            <div class="metrics-list">
              <button
                v-for="s in TIME_STEPS"
                :key="s.id"
                type="button"
                class="metric-chip"
                :class="{ selected: step === s.id }"
                @click="step = s.id"
              >
                {{ s.label }}
              </button>
            </div>
          </template>
          <template v-else-if="!xValues.length && !isXBucket">
            <div class="settings-label sub">Топ значений</div>
            <div class="metrics-list">
              <button
                v-for="top in X_TOPS"
                :key="top"
                type="button"
                class="metric-chip"
                :class="{ selected: xTop === top }"
                @click="xTop = top"
              >
                {{ top }}
              </button>
            </div>
            <div class="settings-hint">По {{ rankingMetricLabel }} — итог за весь период</div>
          </template>
        </div>

        <div class="settings-section">
          <div class="settings-label">Ось Y (строки)</div>
          <select v-model="yDim" class="dim-select">
            <option value="">— без оси Y (одна строка «Все») —</option>
            <option v-for="d in entityDimOptions" :key="d.id" :value="d.id">{{ d.label }}</option>
          </select>
          <template v-if="yDim && !yValues.length && !isYBucket">
            <div class="settings-label sub">Топ значений</div>
            <div class="metrics-list">
              <button
                v-for="top in Y_TOPS"
                :key="top"
                type="button"
                class="metric-chip"
                :class="{ selected: yTop === top }"
                @click="yTop = top"
              >
                {{ top }}
              </button>
            </div>
            <div class="settings-hint">По {{ rankingMetricLabel }} — итог за весь период</div>
          </template>
        </div>

        <div class="settings-section">
          <div class="settings-label">Режим графика</div>
          <div class="metrics-list">
            <button
              v-for="mode in availableModes"
              :key="mode.id"
              type="button"
              class="metric-chip mode-chip"
              :class="{ selected: chartMode === mode.id }"
              @click="chartMode = mode.id"
            >
              {{ mode.label }}
            </button>
          </div>
          <div class="settings-hint">{{ MODE_HINTS[chartMode] }}</div>
          <template v-if="seriesByRows">
            <div class="settings-label sub">Метрики графика (до {{ MAX_CHART_METRICS }})</div>
            <div v-for="grp in selectedMetricGroups" :key="grp.group" class="metrics-group">
              <div v-if="hasMetricGroups" class="metrics-group-label">{{ grp.group }}</div>
              <div class="metrics-list">
                <button
                  v-for="m in grp.items"
                  :key="m.id"
                  type="button"
                  class="metric-chip"
                  :class="{ selected: chartMetrics.includes(m.id) }"
                  :title="chartMetrics.includes(m.id) ? 'Убрать из графика (Shift+клик — только эта)' : 'Показать на графике'"
                  @click="toggleChartMetric(m.id, $event)"
                >
                  {{ m.label }}
                </button>
              </div>
            </div>
            <div class="settings-hint">
              Первая — основной ряд, остальные — пунктир на правой оси (для
              тепловой карты и стека % используется только первая)
            </div>
          </template>
        </div>

        <div class="settings-section">
          <div class="settings-label">Метрики (таблица)</div>
          <div v-for="grp in metricGroups" :key="grp.group" class="metrics-group">
            <div v-if="hasMetricGroups" class="metrics-group-label">{{ grp.group }}</div>
            <div class="metrics-list">
              <button
                v-for="m in grp.items"
                :key="m.id"
                type="button"
                class="metric-chip"
                :class="{ selected: metricsSel.includes(m.id) }"
                :title="metricsSel.includes(m.id) ? 'Убрать метрику (Shift+клик — только эта)' : m.label"
                @click="toggleMetric(m.id, $event)"
              >
                {{ m.label }}
              </button>
            </div>
          </div>
          <div v-if="metricsSel.length > 8" class="settings-hint warn">
            Выбрано {{ metricsSel.length }} метрик — выгрузка будет медленной
          </div>
          <div class="settings-hint">Shift+клик — оставить только эту метрику</div>
        </div>

        <div v-if="!isTimeX" class="settings-section">
          <div class="settings-label">
            Значения оси X
            <template v-if="xValues.length"> — выбрано {{ xValues.length }} (топ не применяется)</template>
          </div>
          <div class="values-panel">
            <input v-model.trim="xSearch" class="values-search" type="text" placeholder="Поиск значения…" />
            <div class="values-list">
              <label v-for="item in xValueOptions" :key="item.value" class="values-item">
                <input
                  type="checkbox"
                  :checked="xValues.includes(item.value)"
                  @change="toggleValue('x', item.value)"
                />
                <span class="values-label">{{ item.value }}</span>
                <span class="values-count">{{ fmtNum(item.count) }}</span>
              </label>
              <div v-if="!xValueOptions.length" class="settings-hint">Нет значений</div>
            </div>
            <label v-if="pivot?.x?.has_others" class="values-item hide-others">
              <input v-model="xHideOthers" type="checkbox" />
              <span class="values-label">Скрыть «Прочие»</span>
            </label>
            <button v-if="xValues.length" class="btn-clear" type="button" @click="xValues = []">
              Сбросить выбор
            </button>
          </div>
        </div>

        <div v-if="yDim" class="settings-section">
          <div class="settings-label">
            Значения оси Y
            <template v-if="yValues.length"> — выбрано {{ yValues.length }} (топ не применяется)</template>
          </div>
          <div class="values-panel">
            <input v-model.trim="ySearch" class="values-search" type="text" placeholder="Поиск значения…" />
            <div class="values-list">
              <label v-for="item in yValueOptions" :key="item.value" class="values-item">
                <input
                  type="checkbox"
                  :checked="yValues.includes(item.value)"
                  @change="toggleValue('y', item.value)"
                />
                <span class="values-label">{{ item.value }}</span>
                <span class="values-count">{{ fmtNum(item.count) }}</span>
              </label>
              <div v-if="!yValueOptions.length" class="settings-hint">Нет значений</div>
            </div>
            <label v-if="pivot?.y?.has_others" class="values-item hide-others">
              <input v-model="yHideOthers" type="checkbox" />
              <span class="values-label">Скрыть «Прочие»</span>
            </label>
            <button v-if="yValues.length" class="btn-clear" type="button" @click="yValues = []">
              Сбросить выбор
            </button>
          </div>
        </div>
      </div>

      <div class="chart-block">
        <div class="chart-title-row">
          <div class="chart-title">{{ chartTitle }}</div>
          <template v-if="xValues.length || yValues.length">
            <button
              v-for="v in xValues"
              :key="`pin-x-${v}`"
              class="btn-clear"
              type="button"
              :title="`Снять фильтр: ${v}`"
              @click="toggleValue('x', v)"
            >
              ✕ {{ v }}
            </button>
            <button
              v-for="v in yValues"
              :key="`pin-y-${v}`"
              class="btn-clear"
              type="button"
              :title="`Снять фильтр: ${v}`"
              @click="toggleValue('y', v)"
            >
              ✕ {{ v }}
            </button>
            <button class="btn-clear" type="button" @click="clearPins">Сбросить фильтры</button>
          </template>
          <div class="chart-actions">
            <button
              type="button"
              class="chart-action"
              :class="{ active: zoomActive }"
              title="Зум выделением по оси X (клики-фильтры отключены)"
              @click="setZoomMode(true)"
            >
              ⬚ Выделение
            </button>
            <button
              type="button"
              class="chart-action"
              :class="{ active: !zoomActive }"
              title="Обычный курсор: клики по графику пинят фильтры осей"
              @click="setZoomMode(false)"
            >
              ✛ Курсор
            </button>
            <button
              type="button"
              class="chart-action"
              title="Сбросить выделение (зум)"
              @click="resetChartZoom"
            >
              ⟲ Сброс выделения
            </button>
          </div>
        </div>
        <!-- :key — сигнатура структуры (режим+серии+оси): инстанс пересоздаётся
             при несовместимых деревьях опций (heatmap ↔ cartesian, смена
             числа серий/осей) — merge/replaceMerge такие переходы не покрывают,
             а гонка с тултипом роняет axis-tooltip -->
        <VChart
          v-if="hasChartData && !pivotError"
          :key="chartSignature"
          ref="chartRef"
          class="chart"
          :option="chartOption"
          :update-options="{ replaceMerge: ['series', 'yAxis'] }"
          autoresize
          @click="onChartClick"
          @dblclick="onChartDblClick"
        />
        <div v-else-if="isPivotLoading" class="chart-empty">Загрузка…</div>
        <div v-else-if="pivotError" class="chart-empty error">{{ pivotErrorText }}</div>
        <div v-else class="chart-empty">Нет данных по выбранным фильтрам</div>
      </div>
    </div>

    <div class="table-scroll">
      <div v-if="pivot" class="table-toolbar">
        <div class="table-tabs">
          <button
            type="button"
            class="metric-chip"
            :class="{ selected: tableMode === 'matrix' }"
            @click="tableMode = 'matrix'"
          >
            Матрица
          </button>
          <button
            type="button"
            class="metric-chip"
            :class="{ selected: tableMode === 'trips' }"
            @click="tableMode = 'trips'"
          >
            Рейсы
          </button>
        </div>
        <template v-if="tableMode === 'matrix'">
          <span class="table-toolbar-label">Метрика таблицы:</span>
          <select v-model="tableMetric" class="dim-select compact">
            <optgroup
              v-for="grp in selectedMetricGroups"
              :key="grp.group"
              :label="hasMetricGroups ? grp.group : undefined"
            >
              <option v-for="m in grp.items" :key="m.id" :value="m.id">{{ m.label }}</option>
            </optgroup>
          </select>
        </template>
        <template v-else-if="cellDrill">
          <span class="table-toolbar-label">
            Срез: {{ cellDrill.row }} × {{ cellDrill.col }}
          </span>
          <button class="btn-clear" type="button" @click="clearDrill">✕ срез</button>
        </template>
        <button
          class="btn-clear export-btn"
          type="button"
          :disabled="isExporting"
          :title="tableMode === 'matrix' ? 'Матрица — по всем выбранным метрикам (лист на метрику)' : 'Все рейсы текущего среза (сортировка как в таблице)'"
          @click="exportXlsx"
        >
          {{ isExporting ? 'Выгрузка…' : '⤓ xlsx' }}
        </button>
      </div>
      <div v-if="pivot && tableMode === 'matrix'" class="matrix-scroll">
        <table class="stat-table">
        <thead>
          <tr>
            <th class="axis-cell">{{ yTitle }}</th>
            <th
              v-for="(label, j) in tableXLabels"
              :key="label"
              :class="{ clickable: !isTimeX }"
              :title="isTimeX ? label : `Фильтр: только «${label}»`"
              @click="!isTimeX && toggleValue('x', label)"
            >
              {{ label }}{{ !isTimeX && xValues.includes(label) ? ' ✕' : '' }}
            </th>
            <th class="total-cell">Итого</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(label, i) in tableYLabels" :key="label">
            <td
              class="id-cell clickable"
              :title="`Фильтр: только «${label}»`"
              @click="toggleValue('y', label)"
            >
              {{ label }}{{ yValues.includes(label) ? ' ✕' : '' }}
            </td>
            <td
              v-for="(label2, j) in tableXLabels"
              :key="label2"
              class="clickable"
              :title="`${cellTitle(tableCells[i][j])}\nДвойной клик — список рейсов`"
              @dblclick="drillCell(label, label2)"
            >
              {{ fmtMetric(tableCells[i][j][tableMetric], tableMetric) }}
            </td>
            <td class="total-cell">{{ fmtMetric(tableRowsTotals[i][tableMetric], tableMetric) }}</td>
          </tr>
          <tr class="total-row">
            <td class="total-cell">Итого</td>
            <td v-for="(label, j) in tableXLabels" :key="label" class="total-cell">
              {{ fmtMetric(tableColsTotals[j][tableMetric], tableMetric) }}
            </td>
            <td class="total-cell strong">{{ fmtMetric(pivot.grand[tableMetric], tableMetric) }}</td>
          </tr>
        </tbody>
        </table>
      </div>
      <TripsTab
        v-else-if="tableMode === 'trips' && pivot"
        :rows="pivotTripsQuery.data.value?.rows || []"
        :total="pivotTripsQuery.data.value?.total || 0"
        :page="tripsPage"
        :page-size="TRIPS_PAGE_SIZE"
        :sorting="tripsSorting"
        :is-loading="pivotTripsQuery.isFetching.value"
        table-only
        @sorting-changed="onTripsSorting"
        @page-changed="onTripsPage"
      />
      <my-loader v-else-if="isPivotLoading" />
      <div v-else class="empty-table">{{ pivotError ? pivotErrorText : 'Нет данных по заданным фильтрам' }}</div>
    </div>
  </div>
</template>

<script>
import { computed, reactive, ref, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import VChart from '@/components/charts/echarts';
import {
  downloadPivotTripsXlsx,
  downloadPivotXlsx,
  fetchPivot,
  fetchPivotConfig,
  fetchPivotDimensionValues,
  fetchPivotTrips,
} from '@/backend/statisticsApi';
import TripsTab from './TripsTab.vue';
import { fmtMoney, fmtNum, fmtPct } from '../format';
import {
  OTHERS_LABEL,
  axisClickAction,
  pinValue,
  seriesClickAction,
} from './constructorInteractions.mjs';

const TIME_STEPS = [
  { id: 'day', label: 'День' },
  { id: 'week', label: 'Неделя' },
  { id: 'month', label: 'Месяц' },
];

const METRIC_MODES = [
  { id: 'grouped', label: 'Группировка' },
  { id: 'stacked', label: 'Стек' },
  { id: 'percent', label: 'Стек %' },
  { id: 'line', label: 'Линия' },
  { id: 'cumulative', label: 'Накопительно' },
];

const ROWS_MODES_TIME = [
  { id: 'line', label: 'Линия' },
  { id: 'cumulative', label: 'Накопительно' },
  { id: 'grouped', label: 'Группировка' },
  { id: 'stacked', label: 'Стек' },
  { id: 'percent', label: 'Стек %' },
  { id: 'heatmap', label: 'Тепловая карта' },
];

const ROWS_MODES_ENTITY = [
  { id: 'grouped', label: 'Группировка' },
  { id: 'stacked', label: 'Стек' },
  { id: 'percent', label: 'Стек %' },
  { id: 'heatmap', label: 'Тепловая карта' },
];

const MODE_HINTS = {
  grouped: 'Каждая строка оси Y — своя серия баров',
  stacked: 'Бары строк складываются друг на друга — выбирайте метрики одной размерности',
  percent: 'Доля каждой строки в сумме колонки, % (стек до 100%)',
  line: 'Сглаженные линии значений по колонкам',
  cumulative: 'Нарастающий итог по колонкам (линии)',
  heatmap: 'Интенсивность цветом: строки × колонки, одна метрика',
};

const X_TOPS = [5, 10, 20, 30];
const Y_TOPS = [5, 10, 15, 30, 50];
const X_VALUES_CAP = 30;
const Y_VALUES_CAP = 100;

// Формат и цвет метрик: id → деньги / проценты / часы
const MONEY_METRICS = new Set([
  'total_commission',
  'net_commission_rub',
  'avg_commission',
  'avg_customer_gross',
  'breakdown_loss_rub',
  'kpi_fund_put_rub',
  'kpi_fund_take_rub',
  'financial_result_rub',
]);
const PCT_METRICS = new Set([
  'avg_margin_pct',
  'avg_profitability_pct',
  'avg_base_deviation_pct',
]);
const DIGIT_METRICS = new Set(['avg_search_hours']);

const PALETTE = [
  '#007bff', '#12b76a', '#f79009', '#7a5af8', '#0e9384',
  '#f04438', '#53b1fd', '#c01048', '#667085', '#15b79e',
  '#fab38e', '#2970ff',
];

const STATE_STORAGE_KEY = 'statisticsConstructorState';

const MAX_CHART_METRICS = 3;

const DEFAULT_STATE = {
  xDim: 'time',
  yDim: '',
  step: 'day',
  metrics: ['trips'],
  chartMode: 'line',
  chartMetrics: ['trips'],
  xTop: 20,
  yTop: 15,
  xValues: [],
  yValues: [],
  xHideOthers: false,
  yHideOthers: false,
};

function loadState() {
  try {
    const raw = window.localStorage.getItem(STATE_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_STATE };
    // Миграция со старого одиночного chartMetric
    if (!Array.isArray(parsed.chartMetrics)) {
      const single = typeof parsed.chartMetric === 'string' ? parsed.chartMetric : DEFAULT_STATE.chartMetrics[0];
      parsed.chartMetrics = [single];
    }
    return { ...DEFAULT_STATE, ...parsed };
  } catch (e) {
    return { ...DEFAULT_STATE };
  }
}

function fmtMetric(value, metric) {
  if (value === null || value === undefined) return '—';
  if (MONEY_METRICS.has(metric)) return fmtMoney(value);
  if (PCT_METRICS.has(metric)) return fmtPct(value);
  if (DIGIT_METRICS.has(metric)) return fmtNum(value, 1);
  return fmtNum(value);
}

function metricColor(idx) {
  return PALETTE[idx % PALETTE.length];
}

export default {
  name: 'ConstructorTab',
  components: { VChart, TripsTab },
  props: {
    filters: { type: Object, required: true },
    filtersKey: { type: String, required: true },
    isActive: { type: Boolean, default: false },
  },
  setup(props) {
    const stored = loadState();
    const xDim = ref(stored.xDim);
    const yDim = ref(stored.yDim);
    const step = ref(stored.step);
    const metricsSel = ref(Array.isArray(stored.metrics) && stored.metrics.length ? [...stored.metrics] : ['trips']);
    const chartMode = ref(stored.chartMode);
    const chartMetrics = ref(
      Array.isArray(stored.chartMetrics) && stored.chartMetrics.length
        ? [...stored.chartMetrics]
        : ['trips']
    );
    const tableMetric = ref(null);
    const xTop = ref(stored.xTop);
    const yTop = ref(stored.yTop);
    const xValues = ref(Array.isArray(stored.xValues) ? stored.xValues : []);
    const yValues = ref(Array.isArray(stored.yValues) ? stored.yValues : []);
    const xSearch = ref('');
    const ySearch = ref('');
    // Скрытие агрегата «Прочие» на соответствующей оси (не persisted-фильтр,
    // а представление): влияет на график/матрицу, список рейсов не сужает
    const xHideOthers = ref(Boolean(stored.xHideOthers));
    const yHideOthers = ref(Boolean(stored.yHideOthers));
    const chartRef = ref(null);

    // Режим указателя графика: кнопки «Выделение/Курсор/Сброс» в строке
    // заголовка (штатный toolbox перекрывал легенду). «Выделение» включает
    // dataZoomSelect через dispatchAction; пока он активен, клики-фильтры
    // и даблклики игнорируются, чтобы не пинить оси случайно.
    const zoomActive = ref(false);
    const applyZoomCursor = () => {
      chartRef.value?.chart?.dispatchAction({
        type: 'takeGlobalCursor',
        key: 'dataZoomSelect',
        dataZoomSelectActive: zoomActive.value,
      });
    };
    const setZoomMode = (on) => {
      zoomActive.value = Boolean(on);
      applyZoomCursor();
    };
    const resetChartZoom = () => {
      zoomActive.value = false;
      applyZoomCursor();
      chartRef.value?.chart?.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
    };

    // Сигнатура структуры графика: при смене режима/числа серий/осей инстанс
    // echarts пересоздаётся с чистой опцией — иначе replaceMerge-удаление
    // серий во время видимого тултипа роняет axis-tooltip («getDataParams,
    // series is undefined»).
    const chartSignature = computed(() => {
      const option = hasChartData.value ? chartOption.value : null;
      const seriesCount = option?.series?.length || 0;
      const yAxis = option?.yAxis;
      const axisCount = Array.isArray(yAxis) ? yAxis.length : yAxis ? 1 : 0;
      return [chartMode.value, seriesCount, axisCount].join('-');
    });

    const state = reactive({
      xDim, yDim, step, metricsSel, chartMode, chartMetrics,
      xTop, yTop, xValues, yValues, xHideOthers, yHideOthers,
    });
    watch(
      state,
      (value) => {
        try {
          window.localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(value));
        } catch (e) {
          /* localStorage может быть недоступен — молча игнорируем */
        }
      },
      { deep: true }
    );

    const configQuery = useQuery({
      queryKey: ['statistics', 'pivot-config'],
      queryFn: fetchPivotConfig,
      enabled: computed(() => props.isActive),
      staleTime: 10 * 60 * 1000,
    });
    const config = computed(() => configQuery.data.value || null);
    const dimensions = computed(() => config.value?.dimensions || []);
    const metricMeta = computed(() => config.value?.metrics || []);
    // Группировка метрик (Объёмы/Деньги/…): порядок — config.metric_groups,
    // затем прочие встречающиеся; старый конфиг без групп → одна секция.
    const metricGroups = computed(() => {
      const order = config.value?.metric_groups || [];
      const byGroup = new Map();
      metricMeta.value.forEach((m) => {
        const g = m.group || 'Прочие';
        if (!byGroup.has(g)) byGroup.set(g, []);
        byGroup.get(g).push(m);
      });
      const rest = [...byGroup.keys()].filter((g) => !order.includes(g));
      return [...order.filter((g) => byGroup.has(g)), ...rest].map((g) => ({
        group: g,
        items: byGroup.get(g),
      }));
    });
    const hasMetricGroups = computed(() => metricGroups.value.length > 1);
    const selectedMetricGroups = computed(() => {
      const sel = new Set(metricsSel.value);
      return metricGroups.value
        .map(({ group, items }) => ({
          group,
          items: items.filter((m) => sel.has(m.id)),
        }))
        .filter((g) => g.items.length);
    });
    const dimOptions = computed(() => dimensions.value);
    // Оси без времени: сущности И бакеты (бакет разрешён на Y как
    // упорядоченная псевдо-сущность)
    const entityDimOptions = computed(() => dimensions.value.filter((d) => d.kind !== 'time'));
    const dimKind = (id) => dimensions.value.find((d) => d.id === id)?.kind || 'entity';
    const isXBucket = computed(() => dimKind(xDim.value) === 'bucket');
    const isYBucket = computed(() => dimKind(yDim.value) === 'bucket');

    const dimLabel = (id) => {
      const dim = dimensions.value.find((d) => d.id === id);
      return dim ? dim.label : id;
    };

    const isTimeX = computed(() => xDim.value === 'time');

    // Санитайз сохранённого состояния после загрузки конфига
    watch(config, (cfg) => {
      if (!cfg) return;
      const dimIds = new Set(cfg.dimensions.map((d) => d.id));
      const metricIds = new Set(cfg.metrics.map((m) => m.id));
      if (!dimIds.has(xDim.value)) xDim.value = 'time';
      if (yDim.value && !dimIds.has(yDim.value)) yDim.value = '';
      const validMetrics = metricsSel.value.filter((m) => metricIds.has(m));
      metricsSel.value = validMetrics.length ? validMetrics : ['trips'];
      const validChart = chartMetrics.value.filter(
        (m) => metricIds.has(m) && metricsSel.value.includes(m)
      );
      chartMetrics.value = validChart.length ? validChart : [metricsSel.value[0]];
    });

    // Смена измерения сбрасывает закреплённые значения и скрытие «Прочие»
    watch(xDim, () => {
      xValues.value = [];
      xHideOthers.value = false;
    });
    watch(yDim, () => {
      yValues.value = [];
      yHideOthers.value = false;
    });
    watch(metricsSel, (value) => {
      const kept = chartMetrics.value.filter((m) => value.includes(m));
      chartMetrics.value = kept.length ? kept : [value[0]];
      if (!value.includes(tableMetric.value)) {
        tableMetric.value = seriesByRows.value ? chartMetrics.value[0] : value[0];
      }
    });

    const toggleChartMetric = (id, event) => {
      if (event && event.shiftKey) {
        chartMetrics.value = [id];
        return;
      }
      const current = chartMetrics.value;
      if (current.includes(id)) {
        if (current.length === 1) return;
        chartMetrics.value = current.filter((m) => m !== id);
        return;
      }
      if (current.length >= MAX_CHART_METRICS) return;
      chartMetrics.value = [...current, id];
    };

    const seriesByRows = computed(() => Boolean(yDim.value));
    const availableModes = computed(() => {
      if (!seriesByRows.value) return METRIC_MODES;
      return isTimeX.value ? ROWS_MODES_TIME : ROWS_MODES_ENTITY;
    });
    watch(availableModes, (modes) => {
      if (!modes.some((m) => m.id === chartMode.value)) {
        chartMode.value = modes[0].id;
      }
    });

    // Порядок метрик в запросе = ранжирование топ-N на бэке (первая метрика).
    // При наличии оси Y первой идёт основная метрика графика — топ-N всегда
    // отсортирован по тому, что видит пользователь; смена переранжирует оси.
    const metricsPayload = computed(() => {
      if (!seriesByRows.value) return metricsSel.value;
      const first = chartMetrics.value[0];
      return [first, ...metricsSel.value.filter((m) => m !== first)];
    });

    const pivotQuery = useQuery({
      queryKey: computed(() => [
        'statistics',
        'pivot',
        props.filtersKey,
        xDim.value,
        yDim.value,
        isTimeX.value ? step.value : '',
        metricsPayload.value.join(','),
        xValues.value.join('\u0001'),
        yValues.value.join('\u0001'),
        xValues.value.length ? '' : xTop.value,
        yValues.value.length ? '' : yTop.value,
      ]),
      queryFn: () =>
        fetchPivot({
          filters: props.filters,
          x: xDim.value,
          y: yDim.value || null,
          metrics: metricsPayload.value,
          step: step.value,
          xTop: xTop.value,
          yTop: yTop.value,
          xValues: xValues.value.length ? xValues.value : null,
          yValues: yValues.value.length ? yValues.value : null,
        }),
      enabled: computed(() => props.isActive && Boolean(config.value)),
      staleTime: 60 * 1000,
      placeholderData: (previousData) => previousData,
    });
    const pivot = computed(() => pivotQuery.data.value || null);
    const isPivotLoading = computed(() => pivotQuery.isLoading.value);
    const pivotError = computed(() => Boolean(pivotQuery.error.value));
    const pivotErrorText = computed(() => {
      const error = pivotQuery.error.value;
      const detail = error?.response?.data?.detail;
      return detail || error?.message || 'Не удалось загрузить данные';
    });

    const xValuesQuery = useQuery({
      queryKey: computed(() => ['statistics', 'pivot-values', xDim.value, props.filtersKey]),
      queryFn: () => fetchPivotDimensionValues(props.filters, xDim.value, 1000),
      enabled: computed(() => props.isActive && !isTimeX.value),
      staleTime: 60 * 1000,
    });
    const yValuesQuery = useQuery({
      queryKey: computed(() => ['statistics', 'pivot-values', yDim.value, props.filtersKey]),
      queryFn: () => fetchPivotDimensionValues(props.filters, yDim.value, 1000),
      enabled: computed(() => props.isActive && Boolean(yDim.value)),
      staleTime: 60 * 1000,
    });

    const filterValueOptions = (items, search, selected) => {
      const list = items.data?.value || [];
      const selectedSet = new Set(selected.value);
      const queryText = search.value.toLowerCase();
      return list.filter(
        (item) => selectedSet.has(item.value) || !queryText || String(item.value).toLowerCase().includes(queryText)
      );
    };
    const xValueOptions = computed(() => filterValueOptions(xValuesQuery, xSearch, xValues));
    const yValueOptions = computed(() => filterValueOptions(yValuesQuery, ySearch, yValues));

    const toggleValue = (axis, value) => {
      const selected = axis === 'x' ? xValues : yValues;
      const cap = axis === 'x' ? X_VALUES_CAP : Y_VALUES_CAP;
      const idx = selected.value.indexOf(value);
      if (idx >= 0) {
        selected.value.splice(idx, 1);
        return;
      }
      if (selected.value.length >= cap) return;
      selected.value = [...selected.value, value];
    };

    const toggleMetric = (id, event) => {
      if (event && event.shiftKey) {
        metricsSel.value = [id];
        return;
      }
      if (metricsSel.value.includes(id)) {
        if (metricsSel.value.length === 1) return;
        metricsSel.value = metricsSel.value.filter((m) => m !== id);
        return;
      }
      metricsSel.value = [...metricsSel.value, id];
    };

    const xLabels = computed(() => pivot.value?.x?.labels || []);
    const yLabels = computed(() => pivot.value?.y?.labels || []);
    // Индексы осей без «Прочие», если ось их скрыла (режим представления)
    const xIdxEff = computed(() => {
      if (xHideOthers.value && pivot.value?.x?.has_others) {
        const skip = xLabels.value.indexOf(OTHERS_LABEL);
        if (skip >= 0) return xLabels.value.map((_, i) => i).filter((i) => i !== skip);
      }
      return xLabels.value.map((_, idx) => idx);
    });
    const yIdxEff = computed(() => {
      if (yHideOthers.value && pivot.value?.y?.has_others) {
        const skip = yLabels.value.indexOf(OTHERS_LABEL);
        if (skip >= 0) return yLabels.value.map((_, i) => i).filter((i) => i !== skip);
      }
      return yLabels.value.map((_, idx) => idx);
    });

    // Карта «имя серии → метка строки Y» для кликов-фильтров по графику.
    // Комбо-серии «{Y} · {метрика}» указывают на свою строку Y; в режиме
    // без оси Y и для метрик-серий карты нет — клики игнорируются.
    // __rows — метки строк для heatmap (клик по ячейке через индексы value).
    const seriesRowMap = computed(() => {
      const map = {};
      if (!pivot.value || !seriesByRows.value) return map;
      yIdxEff.value.forEach((rowIdx) => {
        const label = yLabels.value[rowIdx];
        map[label] = label;
        chartMetrics.value.slice(1).forEach((metricId) => {
          map[`${label} · ${metricLabelById(metricId)}`] = label;
        });
      });
      if (chartMode.value === 'heatmap') {
        map.__rows = yIdxEff.value.map((idx) => yLabels.value[idx]);
      }
      return map;
    });

    // Клик по графику → пин значения оси в боковом меню (кросс-фильтр):
    // клик по серии/ячейке — строка Y, по подписи оси — значение этой оси;
    // Ctrl/Cmd — добавить/убрать в мультивыбор, повторный одиночный клик
    // по единственному запиненному — снять. Время и «Прочие» игнорируются.
    const applyPin = (axis, value, additive) => {
      if (axis === 'x' && isTimeX.value) return;
      const selected = axis === 'x' ? xValues : yValues;
      const cap = axis === 'x' ? X_VALUES_CAP : Y_VALUES_CAP;
      selected.value = pinValue(selected.value, value, { additive, cap });
    };
    const onChartClick = (params) => {
      if (zoomActive.value) return;
      const domEvent = params?.event?.event || null;
      let action = seriesClickAction(params, seriesRowMap.value, domEvent);
      if (!action) action = axisClickAction(params, 'x', domEvent);
      if (!action) action = axisClickAction(params, 'y', domEvent);
      if (!action) return;
      applyPin(action.axis, action.value, action.additive);
    };

    // Даблклик по ячейке тепловой карты → срез в список рейсов
    const onChartDblClick = (params) => {
      if (zoomActive.value || params?.seriesType !== 'heatmap') return;
      const raw = Array.isArray(params.value) ? params.value : [];
      const rowIdx = yIdxEff.value[raw[1]];
      const colIdx = xIdxEff.value[raw[0]];
      if (rowIdx == null || colIdx == null) return;
      drillCell(yLabels.value[rowIdx], xLabels.value[colIdx]);
    };
    const clearPins = () => {
      xValues.value = [];
      yValues.value = [];
    };
    // Смена пинов сбрасывает даблклик-срез (он задавался поверх прошлых пинов)
    watch([xValues, yValues], () => {
      cellDrill.value = null;
    }, { deep: true });

    const selectedMetricMeta = computed(() =>
      metricsSel.value
        .map((id) => metricMeta.value.find((m) => m.id === id))
        .filter(Boolean)
    );
    const metricIdx = (id) => metricMeta.value.findIndex((m) => m.id === id);
    // Метрика ранжирования топ-N (первая в запросе): основная метрика
    // графика при наличии оси Y, иначе первая выбранная.
    const rankingMetricLabel = computed(() => {
      const id = seriesByRows.value ? chartMetrics.value[0] : metricsSel.value[0];
      const meta = metricMeta.value.find((m) => m.id === id);
      return (meta ? meta.label : id || '—').toLowerCase();
    });
    // Метрика отображения ячеек таблицы: независимо от графика, по умолчанию
    // основная метрика графика (при оси Y) или первая выбранная.
    watch(
      [seriesByRows, chartMetrics, metricsSel],
      ([byRows, chart, sel]) => {
        if (!sel.includes(tableMetric.value)) {
          tableMetric.value = byRows ? chart[0] : sel[0];
        }
      },
      { immediate: true }
    );

    const cellValue = (row, col, metric) => {
      const cell = pivot.value?.cells?.[row]?.[col];
      if (!cell) return null;
      const value = cell[metric];
      return value === null || value === undefined ? null : Number(value);
    };

    const cumulativeSeries = (values) => {
      let acc = 0;
      return values.map((v) => {
        acc += Number.isFinite(v) ? v : 0;
        return acc;
      });
    };

    // Стек %: доли слоёв в сумме колонки (нормализация к 100)
    const percentStack = (rowsOfValues) => {
      const totals = rowsOfValues[0].map((_, i) =>
        rowsOfValues.reduce((acc, vals) => acc + (Number.isFinite(vals[i]) ? vals[i] : 0), 0)
      );
      return rowsOfValues.map((vals) =>
        vals.map((v, i) => (totals[i] ? ((Number.isFinite(v) ? v : 0) / totals[i]) * 100 : 0))
      );
    };

    const metricLabelById = (id) => {
      const meta = metricMeta.value.find((m) => m.id === id);
      return meta ? meta.label : id;
    };

    const chartTitle = computed(() => {
      const y = yDim.value ? dimLabel(yDim.value) : 'Все';
      return `${dimLabel(xDim.value)} × ${y}`;
    });
    const yTitle = computed(() => (yDim.value ? dimLabel(yDim.value) : 'Все'));

    const hasChartData = computed(() => {
      if (!pivot.value) return false;
      return xIdxEff.value.length > 0 && yIdxEff.value.length > 0;
    });

    // Пересоздание инстанса графика (:key) сбрасывает и режим выделения.
    // ВАЖНО: watch объявляется ЗДЕСЬ (после hasChartData/chartOption) —
    // computed-геттер вычисляется сразу и ловит TDZ на ранних объявлениях.
    watch(chartSignature, () => {
      zoomActive.value = false;
    });

    const chartOption = computed(() => {
      if (!pivot.value) return {};
      const cols = xIdxEff.value.map((idx) => xLabels.value[idx]);
      const mode = chartMode.value;

      if (mode === 'heatmap') {
        const rows = yIdxEff.value.map((idx) => yLabels.value[idx]);
        const data = [];
        let min = Infinity;
        let max = -Infinity;
        yIdxEff.value.forEach((rowIdx, ri) => {
          xIdxEff.value.forEach((colIdx, ci) => {
            const value = cellValue(rowIdx, colIdx, chartMetrics.value[0]);
            if (value !== null) {
              min = Math.min(min, value);
              max = Math.max(max, value);
            }
            data.push([ci, ri, value === null ? '-' : value]);
          });
        });
        if (!Number.isFinite(min)) {
          min = 0;
          max = 0;
        }
        if (max <= min) {
          max = min + 1;
        }
        return {
          grid: { left: 8, right: 16, top: 24, bottom: 60, containLabel: true },
          dataZoom: [
            { type: 'inside', filterMode: 'none', yAxisIndex: 'none' },
          ],
          // Невидимый, но смонтированный toolbox: фича dataZoom обязана
          // существовать, иначе takeGlobalCursor (кнопка «Выделение») —
          // no-op (show:false даёт ранний return в ToolboxView, фича
          // не инициализируется и BrushController не создаётся).
          toolbox: {
            right: 0,
            top: 0,
            itemSize: 1,
            itemGap: 0,
            iconStyle: { opacity: 0 },
            feature: { dataZoom: {} },
          },
          tooltip: {
            // явно 'item': без этого merge тянет trigger:'axis' из
            // предыдущего режима и форматер получает массив вместо точки
            trigger: 'item',
            confine: true,
            formatter: (p) => {
              const point = Array.isArray(p) ? p[0] : p;
              const value = Array.isArray(point && point.value) ? point.value : [];
              const rowIdx = yIdxEff.value[value[1]];
              const colIdx = xIdxEff.value[value[0]];
              if (rowIdx === undefined || colIdx === undefined) return '';
              const cell = pivot.value.cells[rowIdx][colIdx] || {};
              const lines = selectedMetricMeta.value.map(
                (m) => `${m.label}: <b>${fmtMetric(cell[m.id], m.id)}</b>`
              );
              const hint = '<br/><span style="color:#98a2b3;font-size:11px">клик — фильтр по строке</span>';
              return `${yLabels.value[rowIdx]} × ${xLabels.value[colIdx]}<br/>${lines.join('<br/>')}${hint}`;
            },
          },
          visualMap: {
            min,
            max,
            calculable: true,
            orient: 'horizontal',
            left: 'center',
            bottom: 0,
            inRange: { color: ['#eff6ff', '#3b82f6', '#1d2939'] },
          },
          xAxis: { type: 'category', data: cols, triggerEvent: true, axisLabel: { rotate: 45, fontSize: 10 } },
          yAxis: { type: 'category', data: rows, triggerEvent: true, axisLabel: { fontSize: 10 }, splitArea: { show: true } },
          series: [
            {
              type: 'heatmap',
              data,
              label: { show: false },
            },
          ],
        };
      }

      let series;
      let legend = false;
      // карта имя серии → id метрики для тултипа (id серий в echarts обязаны
      // быть уникальны, поэтому метку метрики несём отдельно)
      const metricBySeriesName = {};
      if (seriesByRows.value) {
        // Комбо: первая метрика графика — основной ряд (бары/линии по строкам
        // оси Y), дополнительные — пунктирные линии на правой оси.
        const primary = chartMetrics.value[0];
        const extras = mode === 'percent' ? [] : chartMetrics.value.slice(1);
        const primaryIsLine = mode === 'line' || mode === 'cumulative';
        let rawPrimary = yIdxEff.value.map((rowIdx) =>
          xIdxEff.value.map((colIdx) => cellValue(rowIdx, colIdx, primary))
        );
        if (mode === 'percent') rawPrimary = percentStack(rawPrimary);
        const primaryData = rawPrimary.map((values) =>
          mode === 'cumulative' ? cumulativeSeries(values) : values
        );
        series = yIdxEff.value.map((rowIdx, ri) => {
          const color = metricColor(ri);
          const name = yLabels.value[rowIdx];
          metricBySeriesName[name] = primary;
          const base = { name, data: primaryData[ri] };
          if (primaryIsLine) {
            return {
              ...base,
              type: 'line',
              smooth: mode === 'line',
              itemStyle: { color },
              lineStyle: { color, width: 2 },
              symbol: 'circle',
              symbolSize: 5,
              connectNulls: true,
            };
          }
          return {
            ...base,
            type: 'bar',
            stack: mode === 'stacked' || mode === 'percent' ? 'total' : undefined,
            itemStyle: { color },
            barMaxWidth: 28,
          };
        });
        extras.forEach((metricId) => {
          yIdxEff.value.forEach((rowIdx, ri) => {
            const color = metricColor(ri);
            const name = `${yLabels.value[rowIdx]} · ${metricLabelById(metricId)}`;
            metricBySeriesName[name] = metricId;
            series.push({
              name,
              type: 'line',
              yAxisIndex: 1,
              data: xIdxEff.value.map((colIdx) => cellValue(rowIdx, colIdx, metricId)),
              itemStyle: { color },
              lineStyle: { color, width: 2, type: 'dashed' },
              symbol: 'circle',
              symbolSize: 4,
              connectNulls: true,
            });
          });
        });
        legend = series.length > 1;
      } else {
        let raws = selectedMetricMeta.value.map((metric) =>
          xIdxEff.value.map((colIdx) => cellValue(0, colIdx, metric.id))
        );
        if (mode === 'percent') raws = percentStack(raws);
        series = selectedMetricMeta.value.map((metric, mi) => {
          const values = mode === 'cumulative' ? cumulativeSeries(raws[mi]) : raws[mi];
          const color = metricColor(metricIdx(metric.id) >= 0 ? metricIdx(metric.id) : mi);
          metricBySeriesName[metric.label] = metric.id;
          if (mode === 'line' || mode === 'cumulative') {
            return {
              name: metric.label,
              type: 'line',
              smooth: mode === 'line',
              data: values,
              itemStyle: { color },
              lineStyle: { color, width: 2 },
              symbol: 'circle',
              symbolSize: 5,
              connectNulls: true,
            };
          }
          return {
            name: metric.label,
            type: 'bar',
            stack: mode === 'stacked' || mode === 'percent' ? 'total' : undefined,
            data: values,
            itemStyle: { color },
            barMaxWidth: 28,
          };
        });
        legend = selectedMetricMeta.value.length > 1;
      }

      const pctFormat = (v) =>
        `${(Number(v) || 0).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;

      // Ось Y: всегда МАССИВ (1 или 2 оси) — смена формы массив↔объект
      // ломает merge в echarts («yAxis 0 not found»); правая ось — для
      // дополнительных метрик графика (другой масштаб, напр. рейсы/руб.)
      const primaryAxisMetric = seriesByRows.value ? chartMetrics.value[0] : metricsSel.value[0];
      const extraAxisMetric = seriesByRows.value ? chartMetrics.value[1] : null;
      const hasExtras = Boolean(extraAxisMetric) && mode !== 'percent';
      const yAxisConfig = [
        {
          type: 'value',
          max: hasExtras ? undefined : mode === 'percent' ? 100 : undefined,
          axisLabel: {
            formatter:
              !hasExtras && mode === 'percent'
                ? '{value}%'
                : (v) => fmtMetric(v, primaryAxisMetric),
          },
        },
      ];
      if (hasExtras) {
        yAxisConfig.push({
          type: 'value',
          axisLabel: { formatter: (v) => fmtMetric(v, extraAxisMetric) },
          splitLine: { show: false },
        });
      }

      return {
        grid: { left: 8, right: 16, top: legend ? 32 : 24, bottom: 50, containLabel: true },
        dataZoom: [
          { type: 'inside', filterMode: 'none' },
          { type: 'slider', height: 18, bottom: 8, filterMode: 'none' },
        ],
        // Невидимый, но смонтированный toolbox (см. heatmap-ветку):
        // без фичи dataZoom кнопка «Выделение» (takeGlobalCursor) — no-op.
        toolbox: {
          right: 0,
          top: 0,
          itemSize: 1,
          itemGap: 0,
          iconStyle: { opacity: 0 },
          feature: { dataZoom: {} },
        },
        legend: legend ? { top: 0, left: 0, right: 0, type: 'scroll', textStyle: { fontSize: 11 } } : undefined,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          confine: true,
          formatter: (params) => {
            const list = Array.isArray(params) ? params : [params];
            const header = list[0].name;
            const fallback = seriesByRows.value ? chartMetrics.value[0] : metricsSel.value[0];
            const lines = list.map((p) => {
              const metric = metricBySeriesName[p.seriesName] || fallback;
              const text = mode === 'percent' ? pctFormat(p.value) : fmtMetric(p.value, metric);
              return `${p.marker} ${p.seriesName}: <b>${text}</b>`;
            });
            const hint = seriesByRows.value
              ? '<br/><span style="color:#98a2b3;font-size:11px">клик — фильтр по строке, подпись оси — по колонке</span>'
              : '<br/><span style="color:#98a2b3;font-size:11px">клик по подписи оси — фильтр по колонке</span>';
            return `${header}<br/>${lines.join('<br/>')}${hint}`;
          },
        },
        xAxis: { type: 'category', data: cols, triggerEvent: true, axisLabel: { rotate: 45, fontSize: 10 } },
        yAxis: yAxisConfig,
        series,
      };
    });

    // Матрица под графиком: строки/колонки с фокусом + итоги
    const tableXLabels = computed(() => xIdxEff.value.map((idx) => xLabels.value[idx]));
    const tableYLabels = computed(() => yIdxEff.value.map((idx) => yLabels.value[idx]));
    const tableCells = computed(() => {
      if (!pivot.value) return [];
      return yIdxEff.value.map((rowIdx) =>
        xIdxEff.value.map((colIdx) => pivot.value.cells[rowIdx][colIdx] || {})
      );
    });
    const tableRowsTotals = computed(() => {
      if (!pivot.value) return [];
      return yIdxEff.value.map((idx) => pivot.value.row_totals[idx] || {});
    });
    const tableColsTotals = computed(() => {
      if (!pivot.value) return [];
      return xIdxEff.value.map((idx) => pivot.value.col_totals[idx] || {});
    });

    const cellTitle = (cell) =>
      selectedMetricMeta.value
        .map((m) => `${m.label}: ${fmtMetric(cell[m.id], m.id)}`)
        .join('\n');

    // ---------------------------------------------------------------- //
    // Вкладка «Рейсы»: список рейсов по состоянию графика
    // ---------------------------------------------------------------- //
    const TRIPS_PAGE_SIZE = 50;
    const tableMode = ref('matrix'); // 'matrix' | 'trips'
    const tripsPage = ref(1);
    const tripsSorting = ref([{ id: 'commission_rub', desc: true }]);
    // Срез даблклика ячейки { row, col } | null — переопределяет пины/фокус
    const cellDrill = ref(null);

    const drillCell = (row, col) => {
      cellDrill.value = { row, col };
      tableMode.value = 'trips';
      tripsPage.value = 1;
    };

    // Срез осей для списка рейсов: пины осей; cellDrill переопределяет всё
    const tripsSlice = computed(() => {
      const x = { values: null, timeBins: null };
      const y = { values: null };
      if (cellDrill.value) {
        if (isTimeX.value) x.timeBins = [cellDrill.value.col];
        else x.values = [cellDrill.value.col];
        if (yDim.value && cellDrill.value.row !== 'Все') {
          y.values = [cellDrill.value.row];
        }
        return { x, y };
      }
      if (!isTimeX.value && xValues.value.length) x.values = [...xValues.value];
      if (yDim.value && yValues.value.length) y.values = [...yValues.value];
      return { x, y };
    });

    // «Прочие» в пинах — спец-метка: known передаём всегда, бэк сам исключит
    const tripsXKnown = computed(() =>
      (!isTimeX.value && pivot.value?.x?.labels) || null
    );
    const tripsYKnown = computed(() =>
      (yDim.value && pivot.value?.y?.labels) || null
    );

    const tripsSortBy = computed(() => tripsSorting.value[0]?.id || 'commission_rub');
    const tripsAsc = computed(() => !tripsSorting.value[0]?.desc);

    const pivotTripsQuery = useQuery({
      queryKey: computed(() => [
        'statistics',
        'pivot-trips',
        props.filtersKey,
        xDim.value,
        yDim.value,
        isTimeX.value ? step.value : '',
        tripsSlice.value.x.values?.join('\u0001') || '',
        tripsSlice.value.x.timeBins?.join('\u0001') || '',
        tripsSlice.value.y.values?.join('\u0001') || '',
        tripsXKnown.value?.join('\u0001') || '',
        tripsYKnown.value?.join('\u0001') || '',
        tripsSortBy.value,
        tripsAsc.value,
        tripsPage.value,
      ]),
      queryFn: () =>
        fetchPivotTrips({
          filters: props.filters,
          x: xDim.value,
          y: yDim.value || null,
          step: step.value,
          xValues: tripsSlice.value.x.values,
          xKnown: tripsXKnown.value,
          yValues: tripsSlice.value.y.values,
          yKnown: tripsYKnown.value,
          timeBins: tripsSlice.value.x.timeBins,
          sortBy: tripsSortBy.value,
          asc: tripsAsc.value,
          limit: TRIPS_PAGE_SIZE,
          offset: (tripsPage.value - 1) * TRIPS_PAGE_SIZE,
        }),
      staleTime: 60 * 1000,
      placeholderData: (prev) => prev,
      enabled: computed(
        () => props.isActive && tableMode.value === 'trips' && Boolean(pivot.value)
      ),
    });

    watch(
      () => [tripsSlice.value, tripsSortBy.value, tripsAsc.value],
      () => {
        tripsPage.value = 1;
      },
      { deep: true }
    );

    const onTripsSorting = (next) => {
      tripsSorting.value = next;
      tripsPage.value = 1;
    };
    const onTripsPage = (page) => {
      tripsPage.value = page;
    };
    const clearDrill = () => {
      cellDrill.value = null;
    };

    // Экспорт активной таблицы (матрица или рейсы) в xlsx — на бекенде
    const isExporting = ref(false);
    const exportXlsx = async () => {
      if (isExporting.value) return;
      isExporting.value = true;
      try {
        if (tableMode.value === 'matrix') {
          await downloadPivotXlsx({
            filters: props.filters,
            x: xDim.value,
            y: yDim.value || null,
            metrics: metricsSel.value,
            step: step.value,
            xTop: xTop.value,
            yTop: yTop.value,
            xValues: xValues.value.length ? xValues.value : null,
            yValues: yValues.value.length ? yValues.value : null,
          });
        } else {
          await downloadPivotTripsXlsx({
            filters: props.filters,
            x: xDim.value,
            y: yDim.value || null,
            step: step.value,
            xValues: tripsSlice.value.x.values,
            xKnown: tripsXKnown.value,
            yValues: tripsSlice.value.y.values,
            yKnown: tripsYKnown.value,
            timeBins: tripsSlice.value.x.timeBins,
            sortBy: tripsSortBy.value,
            asc: tripsAsc.value,
          });
        }
      } finally {
        isExporting.value = false;
      }
    };

    return {
      TIME_STEPS,
      MODE_HINTS,
      X_TOPS,
      Y_TOPS,
      xDim,
      yDim,
      step,
      metricsSel,
      chartMode,
      chartMetrics,
      toggleChartMetric,
      MAX_CHART_METRICS,
      xTop,
      yTop,
      xValues,
      yValues,
      xSearch,
      ySearch,
      chartRef,
      dimOptions,
      entityDimOptions,
      isXBucket,
      isYBucket,
      metricMeta,
      metricGroups,
      hasMetricGroups,
      selectedMetricGroups,
      selectedMetricMeta,
      isTimeX,
      seriesByRows,
      availableModes,
      pivot,
      isPivotLoading,
      pivotError,
      pivotErrorText,
      xValueOptions,
      yValueOptions,
      chartTitle,
      yTitle,
      hasChartData,
      chartOption,
      tableMetric,
      rankingMetricLabel,
      tableXLabels,
      tableYLabels,
      tableCells,
      tableRowsTotals,
      tableColsTotals,
      toggleValue,
      toggleMetric,
      onChartClick,
      onChartDblClick,
      zoomActive,
      setZoomMode,
      resetChartZoom,
      clearPins,
      chartSignature,
      tableMode,
      cellDrill,
      drillCell,
      clearDrill,
      tripsSlice,
      pivotTripsQuery,
      TRIPS_PAGE_SIZE,
      tripsPage,
      tripsSorting,
      onTripsSorting,
      onTripsPage,
      isExporting,
      exportXlsx,
      xHideOthers,
      yHideOthers,
      cellTitle,
      fmtMetric,
      fmtNum,
    };
  },
};
</script>

<style scoped>
.constructor-tab {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
}

.chart-layout {
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 16px;
  align-items: stretch;
}

@media (max-width: 1100px) {
  .chart-layout {
    grid-template-columns: 1fr;
  }
}

.chart-settings {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 620px;
  overflow-y: auto;
}

.settings-title {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.settings-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-label {
  font-size: 12px;
  font-weight: 500;
  color: #667085;
}

.settings-label.sub {
  color: #98a2b3;
  font-weight: 400;
}

.dim-select {
  height: 34px;
  padding: 0 8px;
  font-size: 13px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #101828;
  cursor: pointer;
}

.dim-select:focus {
  outline: none;
  border-color: #84adff;
  box-shadow: 0 0 0 2px rgba(23, 92, 211, 0.12);
}

.metrics-group {
  margin-bottom: 4px;
}

.metrics-group-label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  margin: 6px 0 4px;
}

.metrics-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.metric-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 14px;
  border: 1px solid #d0d5dd;
  background: #fff;
  font-size: 12px;
  color: #344054;
  cursor: pointer;
}

.metric-chip:hover {
  border-color: #98a2b3;
}

.metric-chip.selected {
  border-color: #007bff;
  background: #eff6ff;
}

.settings-hint {
  font-size: 11px;
  color: #98a2b3;
}

.settings-hint.warn {
  color: #d92d20;
}

.values-panel {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.values-search {
  height: 30px;
  padding: 0 10px;
  font-size: 12px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #101828;
}

.values-search:focus {
  outline: none;
  border-color: #84adff;
}

.values-list {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #f2f4f7;
  border-radius: 6px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.values-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 12px;
  color: #344054;
  cursor: pointer;
}

.values-item.hide-others {
  margin-top: 6px;
  border-top: 1px solid #eaecf0;
  padding-top: 8px;
  font-weight: 600;
}

.values-item:hover {
  background: #f8fafc;
}

.values-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.values-count {
  color: #98a2b3;
  font-size: 11px;
}

.btn-clear {
  align-self: flex-start;
  padding: 3px 10px;
  font-size: 11px;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  background: #fff;
  color: #667085;
  cursor: pointer;
}

.btn-clear:hover {
  border-color: #98a2b3;
  color: #344054;
}

.chart-block {
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
  display: flex;
  flex-direction: column;
}

.chart-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.chart-actions {
  margin-left: auto;
  display: flex;
  gap: 6px;
}

.chart-action {
  padding: 3px 10px;
  font-size: 11px;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  background: #fff;
  color: #667085;
  cursor: pointer;
}

.chart-action:hover {
  border-color: #98a2b3;
  color: #344054;
}

.chart-action.active {
  border-color: #1570ef;
  background: #eff8ff;
  color: #1570ef;
}

.table-tabs {
  display: flex;
  gap: 6px;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.chart {
  width: 100%;
  flex: 1;
  min-height: 380px;
}

.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 200px;
  color: #98a2b3;
  font-size: 14px;
}

.chart-empty.error {
  color: #b42318;
}

.table-scroll {
  margin-top: 14px;
}

.matrix-scroll {
  overflow: auto;
  min-height: 300px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.table-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
}

.table-toolbar-label {
  font-size: 12px;
  color: #667085;
}

.export-btn {
  margin-left: auto;
  font-weight: 600;
}

.export-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.dim-select.compact {
  height: 28px;
  font-size: 12px;
}

.stat-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: #344054;
  white-space: nowrap;
}

.stat-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 8px 10px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  color: #475467;
  font-weight: 600;
  text-align: left;
}

.stat-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #f2f4f7;
}

.stat-table tbody tr:hover {
  background: #f8fafc;
}

.stat-table td:first-child,
.stat-table th:first-child {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
}

.stat-table th:first-child {
  background: #f8fafc;
  z-index: 3;
}

.id-cell {
  font-weight: 600;
}

.clickable {
  cursor: pointer;
}

.clickable:hover {
  color: #007bff;
}

.total-cell {
  background: #f8fafc;
  font-weight: 600;
  color: #475467;
}

.strong {
  color: #101828;
}

.total-row td {
  border-top: 2px solid #e5e7eb;
}

.empty-table {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #98a2b3;
}
</style>
